import { useEffect, useState } from 'react'
import { Button, Col, notification, Popconfirm, Row, Table, Tooltip } from 'antd'
import { getPagination, getTableFilter, handleUrlChange } from '@TerraCycleUS/loop-antd-filter'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { isEmpty, orderBy } from 'lodash'
import {
  Can,
  Icon,
  IconContainer,
  PageContainer,
  PageTitleRow,
} from '../../components'
import {
  handleBackendError,
} from '../../utils'
import { withConfig, withProfile } from '../../modules'
import { configPropTypes, userPropTypes } from '../../types'
import AdminDetailsModal from './AdminDetailsModal'
import { createAdmin, deleteAdmin, getAdminList, updateAdmin } from './api'

export const AdminUsers = ({ config, user }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isModalVisible, setModalVisibility] = useState(false)
  const [isModalLoading, setModalLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState()
  const [admins, setAdmins] = useState([])
  const [selectedAdmin, setSelectedAdmin] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const isSuperAdmin = user?.me.roles.includes('super_admin')

  const [metaData, setMetaData] = useState()

  useEffect(() => {
    if (isEmpty(searchParams.toString()))
      setSearchParams({ items_per_page: 25, page: 1, sort_by: 'name ASC' })
  }, [])

  const roles = orderBy(
    config.config?.admin_user_roles.map((admin_user_roles) => ({
      text: admin_user_roles,
      value: admin_user_roles,
    })),
    'text',
    'asc',
  )
  const countries = orderBy(
    config.config?.countries.map((country) => ({
      text: country.name,
      value: country.id,
    })),
    'text',
    'asc',
  )

  const navigate = useNavigate()

  useEffect(() => {
    if (isSuperAdmin) {
      if (!isEmpty(searchParams.toString()))
        fetchAdminList(searchParams.toString())
    } else {
      navigate('/')
    }
  }, [isSuperAdmin, navigate, searchParams])

  const fetchAdminList = (params) => {
    setIsLoading(true)

    getAdminList(params)
      .then(({ data }) => {
        setMetaData(data.meta)
        setAdmins(data.data)
        setIsLoading(false)
      })
      .catch((error) => {
        setIsLoading(false)

        handleFailure(error, 'Fetching admin users failed!')
      })
  }

  const tableColumns = [
    {
      className: 'nameSelenium',
      dataIndex: 'name',
      defaultSortOrder: 'ascend',
      sorter: true,
      title: 'Name',
      ...getTableFilter({ name: 'name' }),
    },
    {
      className: 'emailSelenium',
      dataIndex: 'email',
      title: 'Email',
      ...getTableFilter({ name: 'email' }),
    },
    {
      className: 'roleSelenium',
      dataIndex: 'roles',
      render: (_, record) => <div>{record.roles.join(', ')}</div>,
      title: 'Role',
      ...getTableFilter({filterValues: roles, name: 'roles[]', type: 'checkbox' }),
    },
    {
      className: 'countryStatusSelenium',
      dataIndex: 'countries',
      render: (_, record) => (
        <div>{record.countries?.map((country) => country.name).join(', ')}</div>
      ),
      title: 'Countries',
      ...getTableFilter({filterValues: countries, name: 'country_ids[]', type: 'checkbox' }),
    },
    {
      className: 'countryActionSelenium',
      key: 'action',
      render: (_, record) => (
        <IconContainer>
          <Can
            requiredPermission="admin_user.get.admin_user_id"
            yes={() => (
              <Tooltip title="Edit">
                <Icon
                  data-testid="editBtn"
                  name="updateAdminSelenium"
                  onClick={() => handleOpenModal(record)}
                  type="edit"
                />
              </Tooltip>
            )}
          />

          <Can
            requiredPermission="admin_user.delete.admin_user_id"
            yes={() => (
              <Tooltip title="Delete">
                <Popconfirm
                  cancelText="No"
                  className="action-list-button"
                  okText="Yes"
                  onConfirm={() => handleDeleteAdmin(record)}
                  title="Are you sure you want to delete this admin?"
                >
                  <Icon
                    data-testid="deleteBtn"
                    name="deleteAdminSelenium"
                    type="delete"
                  />
                </Popconfirm>
              </Tooltip>
            )}
          />
        </IconContainer>
      ),
      title: 'Actions',
    },
  ]

  const handleFormSubmit = (actionType, formData) => {
    switch (actionType) {
    case 'UPDATE':
      return updateAdmin(formData)
        .then(() => handleSuccess({ description: 'Admin user updated' }))
        .catch((error) => handleFailure(error, 'Admin user update failed!'))

    case 'CREATE':
      return createAdmin(formData)
        .then(() =>
          handleSuccess({ description: 'Admin user has been created' }),
        )
        .catch((error) =>
          handleFailure(error, 'Admin user creation failed!'),
        )
    default:
      return
    }
  }

  const handleDeleteAdmin = (record) => {
    return deleteAdmin(record.id)
      .then(() => handleSuccess({ description: 'Admin removed' }, true))
      .catch((error) => handleFailure(error, 'Admin removal failed!'))
  }

  const handleOpenModal = (selectedAdmin) => {
    setSelectedAdmin(selectedAdmin)
    setErrorMessage(null)
    setModalVisibility(true)
    setModalLoading(false)
  }

  const handleSuccess = (messageOptions) => {
    fetchAdminList(searchParams.toString())
    setErrorMessage(null)
    setModalVisibility(false)

    notification.success({
      title: 'Success',
      ...messageOptions,
    })
  }

  const handleFailure = (error, message) => {
    if (error) {
      console.log(error)
    }

    const errorMessage = handleBackendError(error, message)

    setErrorMessage(errorMessage)
    setModalLoading(false)

    notification.error({
      description: errorMessage,
      title: 'Error',
    })
  }

  return (
    <PageContainer>
      <PageTitleRow title="Admins">
        <Can
          requiredPermission="admin_user.post.create"
          yes={() => (
            <Button
              name="createAdminSelenium"
              onClick={() => handleOpenModal(null)}
              type="primary"
            >
              NEW ADMIN
            </Button>
          )}
        />
      </PageTitleRow>
      <Row>
        <Col span={24}>
          <Table
            className="adminUserPageTableSelenium"
            columns={tableColumns}
            data-testid="adminPage"
            dataSource={admins}
            loading={isLoading}
            onChange={(pagination, filters, sorter) => handleUrlChange(pagination, filters, sorter, setSearchParams)}
            pagination={getPagination(metaData?.page, metaData?.items_per_page, metaData?.total_items)}
            rowKey="id"
          />

          {isModalVisible && (
            <AdminDetailsModal
              errorMessage={errorMessage}
              hideModal={() => {
                setModalVisibility(false)
                fetchAdminList(searchParams.toString())
              }}
              isModalLoading={isModalLoading}
              onDelete={handleDeleteAdmin}
              onSubmit={handleFormSubmit}
              selectedAdmin={selectedAdmin}
              setModalLoading={setModalLoading}
            />
          )}
        </Col>
      </Row>
    </PageContainer>
  )
}

AdminUsers.propTypes = {
  config: configPropTypes,
  user: userPropTypes,
}

export default withConfig(withProfile(AdminUsers))
